/**
 * WorkflowStepper — Step-by-step status flow with clickable transitions.
 *
 * Shows a horizontal stepper with connected dots/lines.
 * User can click the NEXT step to advance status.
 * Cancel/Reject shown as SEPARATE pill buttons below the stepper.
 */

import { useState } from 'react';
import { Check, ChevronRight, Ban, Loader2, Undo2 } from 'lucide-react';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getStatusConfig } from '@/config/status-registry';
import { getAllowedTransitions, type EntityType } from '@apv-erp/status';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/lib/form/FormFieldError';
import type { WorkflowConfig } from '@/config/workflow-configs';

interface WorkflowStepperProps {
    /** Workflow configuration */
    workflow: WorkflowConfig;
    /** Current status value */
    currentStatus: string;
    /** Callback when user requests status change */
    onStatusChange: (newStatus: string) => Promise<void>;
    /** Disable all interactions */
    disabled?: boolean;
}

type StepState = 'completed' | 'current' | 'next' | 'future' | 'cancelled' | 'rejected' | 'prev';

const COLOR_THEMES = {
    emerald: { currentDot: 'bg-emerald-600 border-emerald-600 ring-emerald-100', nextDot: 'border-emerald-300 text-emerald-500 group-hover:border-emerald-500 group-hover:bg-emerald-50', textCurrent: 'text-emerald-700', textNext: 'text-emerald-500 group-hover:text-emerald-700', arrow: 'text-emerald-400 group-hover:text-emerald-600' },
    indigo: { currentDot: 'bg-indigo-600 border-indigo-600 ring-indigo-100', nextDot: 'border-indigo-300 text-indigo-500 group-hover:border-indigo-500 group-hover:bg-indigo-50', textCurrent: 'text-indigo-700', textNext: 'text-indigo-500 group-hover:text-indigo-700', arrow: 'text-indigo-400 group-hover:text-indigo-600' },
    blue: { currentDot: 'bg-blue-600 border-blue-600 ring-blue-100', nextDot: 'border-blue-300 text-blue-500 group-hover:border-blue-500 group-hover:bg-blue-50', textCurrent: 'text-blue-700', textNext: 'text-blue-500 group-hover:text-blue-700', arrow: 'text-blue-400 group-hover:text-blue-600' },
    cyan: { currentDot: 'bg-cyan-600 border-cyan-600 ring-cyan-100', nextDot: 'border-cyan-300 text-cyan-500 group-hover:border-cyan-500 group-hover:bg-cyan-50', textCurrent: 'text-cyan-700', textNext: 'text-cyan-500 group-hover:text-cyan-700', arrow: 'text-cyan-400 group-hover:text-cyan-600' },
    amber: { currentDot: 'bg-amber-500 border-amber-500 ring-amber-100', nextDot: 'border-amber-300 text-amber-500 group-hover:border-amber-500 group-hover:bg-amber-50', textCurrent: 'text-amber-700', textNext: 'text-amber-500 group-hover:text-amber-700', arrow: 'text-amber-400 group-hover:text-amber-600' },
    orange: { currentDot: 'bg-orange-500 border-orange-500 ring-orange-100', nextDot: 'border-orange-300 text-orange-500 group-hover:border-orange-500 group-hover:bg-orange-50', textCurrent: 'text-orange-700', textNext: 'text-orange-500 group-hover:text-orange-700', arrow: 'text-orange-400 group-hover:text-orange-600' },
    rose: { currentDot: 'bg-rose-600 border-rose-600 ring-rose-100', nextDot: 'border-rose-300 text-rose-500 group-hover:border-rose-500 group-hover:bg-rose-50', textCurrent: 'text-rose-700', textNext: 'text-rose-500 group-hover:text-rose-700', arrow: 'text-rose-400 group-hover:text-rose-600' },
    purple: { currentDot: 'bg-purple-600 border-purple-600 ring-purple-100', nextDot: 'border-purple-300 text-purple-500 group-hover:border-purple-500 group-hover:bg-purple-50', textCurrent: 'text-purple-700', textNext: 'text-purple-500 group-hover:text-purple-700', arrow: 'text-purple-400 group-hover:text-purple-600' },
    teal: { currentDot: 'bg-teal-600 border-teal-600 ring-teal-100', nextDot: 'border-teal-300 text-teal-500 group-hover:border-teal-500 group-hover:bg-teal-50', textCurrent: 'text-teal-700', textNext: 'text-teal-500 group-hover:text-teal-700', arrow: 'text-teal-400 group-hover:text-teal-600' },
    slate: { currentDot: 'bg-slate-600 border-slate-600 ring-slate-100', nextDot: 'border-slate-300 text-slate-500 group-hover:border-slate-500 group-hover:bg-slate-50', textCurrent: 'text-slate-700', textNext: 'text-slate-500 group-hover:text-slate-700', arrow: 'text-slate-400 group-hover:text-slate-600' },
};
type ThemeColor = keyof typeof COLOR_THEMES;

function extractTheme(color?: string): ThemeColor {
    if (!color) return 'indigo';
    for (const key of Object.keys(COLOR_THEMES)) {
        if (color.includes(key)) return key as ThemeColor;
    }
    return 'indigo';
}

/** Các bước hiển thị + bước ứng với trạng thái hiện tại (bí danh, chèn theo thứ tự chuẩn nếu thiếu) */
export function resolveStepperState(workflow: WorkflowConfig, currentStatus: string): { steps: string[]; stepStatus: string } {
    const upper = String(currentStatus || '').toUpperCase();
    const stepStatus = workflow.statusAliases?.[upper] || currentStatus || '';
    const has = (list: string[], st: string) => list.some((x) => x.toUpperCase() === st.toUpperCase());
    let steps = workflow.steps;
    if (stepStatus && !has(steps, stepStatus) && workflow.stepOrder && has(workflow.stepOrder, stepStatus)) {
        const rank = (st: string) => workflow.stepOrder!.findIndex((x) => x.toUpperCase() === st.toUpperCase());
        const target = rank(stepStatus);
        const insertAt = steps.findIndex((x) => rank(x) > target);
        steps = insertAt === -1 ? [...steps, stepStatus] : [...steps.slice(0, insertAt), stepStatus, ...steps.slice(insertAt)];
    }
    return { steps, stepStatus };
}

/** statusMapName → loại chứng từ có máy trạng thái phía server (packages/status STATE_MACHINE_CONFIG) */
const SERVER_STATE_MACHINE: Record<string, EntityType> = {
    QuotationStatus: 'QUOTATION',
    OrderStatus: 'ORDER',
    ProductionOrderStatus: 'PRODUCTION_ORDER',
    PurchaseOrderStatus: 'PURCHASE_ORDER',
    DeliveryStatus: 'DELIVERY',
};

export default function WorkflowStepper({ workflow, currentStatus, onStatusChange, disabled }: WorkflowStepperProps) {
    const [isUpdating, setIsUpdating] = useState(false);
    const [confirmTarget, setConfirmTarget] = useState<string | null>(null);

    const { cancelledStatus, rejectedStatus, pausedStatus, onHoldStatus, statusMapName } = workflow;
    // Trạng thái không nằm trên các bước (quy trình lấy từ DB thiếu bước, trạng thái phụ / cũ) vẫn phải hiện đúng chỗ:
    // trước đây không bước nào sáng và vẫn hiện nút Hủy (FB-JOU4AV — đơn Đã lên KH sau khi tạo lệnh sản xuất)
    const { steps, stepStatus } = resolveStepperState(workflow, currentStatus);

    // Determine state types
    const isCancelled = currentStatus === cancelledStatus;
    const isRejected = currentStatus === rejectedStatus;
    const isPaused = currentStatus === pausedStatus;
    const isOnHold = currentStatus === onHoldStatus;
    const isTerminal = isCancelled || isRejected;
    const isTemporaryStop = isPaused || isOnHold;

    // Find current step index in the flow (case-insensitive for robustness)
    const currentIdx = steps.findIndex(s => s.toUpperCase() === stepStatus.toUpperCase());

    // Determine state for each step
    function getStepState(stepIdx: number): StepState | 'paused' {
        if (isTerminal) return stepIdx <= 0 ? 'completed' : 'future';
        if (isTemporaryStop) {
            return 'paused';
        }
        if (currentIdx < 0) return 'future'; // Unknown status, show all as future
        if (stepIdx === currentIdx - 1 && workflow.allowBack) return 'prev';
        if (stepIdx < currentIdx) return 'completed';
        if (stepIdx === currentIdx) return 'current';
        if (stepIdx === currentIdx + 1) return 'next';
        return 'future';
    }

    // Chỉ cho bấm bước mà máy trạng thái phía server cho phép (tránh bấm rồi bị từ chối, VD đơn Chờ duyệt → Bản nháp).
    // Các chứng từ máy chủ kiểm bằng WorkflowValidator; chứng từ khác giữ như cũ.
    function isTransitionAllowed(step: string): boolean {
        const entity = SERVER_STATE_MACHINE[workflow.statusMapName || ''];
        if (!entity) return true;
        return getAllowedTransitions(entity, currentStatus).includes(step);
    }

    async function handleStepClick(newStatus: string) {
        if (disabled || isUpdating) return;
        setConfirmTarget(newStatus);
    }

    async function confirmChange() {
        if (!confirmTarget || isUpdating) return;
        setIsUpdating(true);
        try {
            await onStatusChange(confirmTarget);
            setConfirmTarget(null); // Clear immediately on success
        } catch (error: any) {
            console.error('Workflow transition failed:', error);
            // Máy chủ từ chối (quy tắc chuyển trạng thái, thiếu điều kiện…): báo đúng lý do, không im lặng
            const serverMsg = error?.response?.data?.message;
            const msg = Array.isArray(serverMsg) ? serverMsg.join('; ') : serverMsg || extractErrorMessage(error);
            toast.error(msg || 'Không chuyển được trạng thái');
            setConfirmTarget(null);
        } finally {
            setIsUpdating(false);
        }
    }

    const confirmLabel = confirmTarget
        ? getStatusConfig(statusMapName, confirmTarget).label
        : '';
    const confirmIsDanger = confirmTarget === cancelledStatus || confirmTarget === rejectedStatus || confirmTarget === pausedStatus || confirmTarget === onHoldStatus;

    // Determine if terminal actions are allowed
    let canCancel = true;
    if (workflow.cancelableUntil) {
        const uncancelIdx = steps.findIndex(s => s.toUpperCase() === workflow.cancelableUntil!.toUpperCase());
        if (uncancelIdx >= 0 && currentIdx > uncancelIdx) {
            canCancel = false;
        }
    } else {
        // Default: cannot cancel if it is the very last step
        if (currentIdx >= steps.length - 1) {
            canCancel = false;
        }
    }

    // Check if there are any terminal/branch actions available
    const hasTerminalActions = !isTerminal && canCancel && (cancelledStatus || rejectedStatus || pausedStatus || onHoldStatus);

    return (
        <>
            {/* ── Main Stepper Flow (happy path only) ── */}
            <div className="flex items-center gap-0 overflow-x-auto py-2 px-1 -mx-1 flex-nowrap">
                {steps.map((step, idx) => {
                    const state = getStepState(idx);
                    const cfg = getStatusConfig(statusMapName, step);
                    const isClickable =
                        !disabled && !isUpdating && (state === 'next' || state === 'prev') && isTransitionAllowed(step);

                    return (
                        <div key={step} className="flex items-center shrink-0">
                            {/* Connector line */}
                            {idx > 0 && (
                                <div className={`h-0.5 w-6 sm:w-10 transition-colors ${state === 'completed' || state === 'current' || state === 'prev'
                                    ? 'bg-emerald-400'
                                    : 'bg-slate-200'
                                    }`} />
                            )}

                            {/* Step dot + label */}
                            <button
                                type="button"
                                data-step={step}
                                data-state={state}
                                disabled={!isClickable}
                                onClick={() => isClickable && handleStepClick(step)}
                                className={`
                                    flex flex-col items-center gap-1 group relative
                                    ${isClickable ? 'cursor-pointer' : 'cursor-default'}
                                `}
                                title={isClickable ? `Chuyển sang: ${cfg.label}` : cfg.label}
                            >
                                {/* Dot */}
                                <div className={`
                                    w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all border-2 relative overflow-hidden
                                    ${state === 'completed' || state === 'prev'
                                        ? `bg-emerald-500 border-emerald-500 text-white ${state === 'prev' && isClickable ? 'hover:bg-amber-500 hover:border-amber-500 ring-2 ring-transparent hover:ring-amber-200 ring-offset-1' : ''}`
                                        : state === 'current'
                                            ? `${COLOR_THEMES[extractTheme(cfg.color)].currentDot} text-white ring-4`
                                            : state === 'next'
                                                ? `bg-white ${COLOR_THEMES[extractTheme(cfg.color)].nextDot} ${isClickable ? 'ring-4 ring-offset-1 animate-[pulse_1.5s_ease-in-out_infinite] shadow-md' : ''} group-hover:scale-110`
                                                : 'bg-white border-slate-200 text-slate-400'
                                    }
                                `}>
                                    {state === 'completed' ? (
                                        <Check className="h-3.5 w-3.5" />
                                    ) : state === 'prev' ? (
                                        <div className="relative w-full h-full flex items-center justify-center">
                                            <Check className={`h-3.5 w-3.5 transition-opacity absolute ${isClickable ? 'group-hover:opacity-0' : ''}`} />
                                            {isClickable && <Undo2 className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity absolute" />}
                                        </div>
                                    ) : state === 'current' ? (
                                        <span className="w-2 h-2 bg-white rounded-full" />
                                    ) : (
                                        <span className="text-[10px]">{idx + 1}</span>
                                    )}
                                </div>

                                {/* Label */}
                                <span className={`
                                    text-[10px] font-medium text-center max-w-[80px] leading-tight break-words whitespace-nowrap
                                    ${state === 'completed' || state === 'prev' ? 'text-emerald-600'
                                        : state === 'current' ? `${COLOR_THEMES[extractTheme(cfg.color)].textCurrent} font-bold`
                                            : state === 'next' ? `${COLOR_THEMES[extractTheme(cfg.color)].textNext} ${isClickable ? 'animate-pulse' : ''}`
                                                : state === 'paused' ? 'text-orange-500 font-bold'
                                                : 'text-slate-400'
                                    }
                                `}>
                                    {cfg.label}
                                </span>

                                {/* Clickable indicator — pulsing arrow for NEXT */}
                                {isClickable && state === 'next' && (
                                    <ChevronRight className={`h-3 w-3 absolute -right-1.5 top-1.5 animate-bounce ${COLOR_THEMES[extractTheme(cfg.color)].arrow}`} />
                                )}
                                {/* Clickable indicator — pulsing arrow for PREV (backwards) */}
                                {isClickable && state === 'prev' && (
                                    <div className="absolute -left-1.5 top-1.5 animate-pulse bg-white rounded-full p-0.5 shadow-sm border border-amber-200 group-hover:border-amber-400">
                                        <Undo2 className="h-2 w-2 text-amber-500" />
                                    </div>
                                )}
                            </button>
                        </div>
                    );
                })}

                {/* ── Terminal Actions (Cancel/Reject/Pause) — INLINE after last step ── */}
                {hasTerminalActions && (
                    <>
                        {/* Separator */}
                        <div className="h-5 w-px bg-slate-200 mx-3 shrink-0" />

                        <div className="flex items-center gap-1.5 shrink-0">
                            {pausedStatus && !isTemporaryStop && (
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all
                                        border-orange-200 text-orange-500 bg-white hover:border-orange-400 hover:text-orange-600 hover:bg-orange-50"
                                    disabled={disabled || isUpdating}
                                    onClick={() => handleStepClick(pausedStatus)}
                                    title="Tạm ngưng"
                                >
                                    <Ban className="h-2.5 w-2.5" />
                                    Tạm ngưng
                                </button>
                            )}
                            {cancelledStatus && (
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all
                                        border-rose-200 text-rose-400 bg-white hover:border-rose-400 hover:text-rose-600 hover:bg-rose-50"
                                    disabled={disabled || isUpdating}
                                    onClick={() => handleStepClick(cancelledStatus)}
                                    title="Hủy"
                                >
                                    <Ban className="h-2.5 w-2.5" />
                                    Hủy
                                </button>
                            )}
                            {rejectedStatus && (
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all
                                        border-amber-200 text-amber-400 bg-white hover:border-amber-400 hover:text-amber-600 hover:bg-amber-50"
                                    disabled={disabled || isUpdating}
                                    onClick={() => handleStepClick(rejectedStatus)}
                                    title="Từ chối"
                                >
                                    <Ban className="h-2.5 w-2.5" />
                                    Từ chối
                                </button>
                            )}
                            {onHoldStatus && !isTemporaryStop && (
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all
                                        border-amber-200 text-amber-500 bg-white hover:border-amber-400 hover:text-amber-600 hover:bg-amber-50"
                                    disabled={disabled || isUpdating}
                                    onClick={() => handleStepClick(onHoldStatus)}
                                    title="Tạm dừng chờ xử lý"
                                >
                                    <Ban className="h-2.5 w-2.5" />
                                    Hold
                                </button>
                            )}
                            {isTemporaryStop && (
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all
                                        border-indigo-200 text-indigo-600 bg-white hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50"
                                    disabled={disabled || isUpdating}
                                    onClick={() => handleStepClick('IN_PROGRESS')}
                                    title="Tiếp tục quy trình"
                                >
                                    <Check className="h-2.5 w-2.5" />
                                    Tiếp tục
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* ── Terminal Status Banner (when already Cancelled/Rejected) ── */}
            {(isTerminal || isTemporaryStop) && (
                <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-md mt-2 ${
                    isCancelled ? 'bg-rose-50 text-rose-700 border border-rose-200'
                    : isRejected ? 'bg-amber-50 text-amber-700 border border-amber-200'
                    : 'bg-orange-50 text-orange-700 border border-orange-200'
                    }`}>
                    <Ban className="h-3.5 w-3.5 shrink-0" />
                    <span className="font-medium">
                        {isCancelled ? 'Đã hủy' : isRejected ? 'Đã từ chối' : isOnHold ? 'Đang tạm dừng (Hold)' : 'Đang tạm ngưng (Paused)'}
                    </span>
                    {!isTemporaryStop && <span className="text-[10px] opacity-70">— Trạng thái kết thúc</span>}
                </div>
            )}

            {/* ── Confirm Dialog ── */}
            <AlertDialog open={!!confirmTarget} onOpenChange={(open) => !open && setConfirmTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {confirmIsDanger ? '⚠️ Xác nhận' : 'Chuyển trạng thái'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Chuyển trạng thái sang <strong className={confirmIsDanger ? 'text-rose-600' : 'text-indigo-600'}>
                                {confirmLabel}
                            </strong>?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isUpdating}>Huỷ</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmChange}
                            disabled={isUpdating}
                            className={confirmIsDanger ? 'bg-rose-600 hover:bg-rose-700' : ''}
                        >
                            {isUpdating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Xác nhận
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
